import React, { useEffect, useState, useCallback, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

// Custom Arrow Components
const PrevArrow = ({ onClick }) => (
  <button
    className="absolute top-1/2 left-0 z-10 bg-gray-800 text-white p-2 rounded-full shadow-lg transform -translate-y-1/2 hover:bg-gray-700"
    onClick={onClick}
    style={{ left: "10px" }}
  >
    <FaArrowLeft size={20} />
  </button>
);

const NextArrow = ({ onClick }) => (
  <button
    className="absolute top-1/2 right-0 z-10 bg-gray-800 text-white p-2 rounded-full shadow-lg transform -translate-y-1/2 hover:bg-gray-700"
    onClick={onClick}
    style={{ right: "10px" }}
  >
    <FaArrowRight size={20} />
  </button>
);

// Product Card Component (Memoized)
const ProductCard = React.memo(({ product }) => (
  <div className="px-4">
    <div className="w-full max-w-[300px] mx-auto shadow-md rounded-md">
      <img
        src={product.image}
        alt={product.name}
        className="w-full h-60 object-contain rounded-md"
        loading="lazy"
      />
      <h5 className="text-md text-center bg-primary font-semibold text-white hover:text-secondary py-4 rounded-md mt-4">
        <a href={`/product/${product.slug}`} className="text-white hover:underline">
          {product.name}
        </a>
      </h5>
    </div>
  </div>
));

const ProductsSection = () => {
  const [products, setProducts] = useState([]);

  // Fetch data only when needed
  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch("/products.json");
      const data = await response.json();
      console.log("Fetched Products:", data);

      if (data.categories) {
        setProducts(data.categories.flatMap((category) => category.products));
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Memoize products array
  const productList = useMemo(() => products, [products]);

  // Slider settings with custom arrows
  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 4,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 3000,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      responsive: [
        { breakpoint: 1024, settings: { slidesToShow: 2 } },
        { breakpoint: 640, settings: { slidesToShow: 1 } },
      ],
    }),
    []
  );

  return (
    <div className="py-28 relative">
      <div className="text-center">
        <h3 className="text-4xl mt-12 mb-8 text-primary font-bold relative inline-block">
          Our
          <span className="absolute bottom-[-8px] left-0 w-12 h-1 bg-secondary"></span>
          <span className="text-secondary"> Products</span>
        </h3>
      </div>

      {/* Product Slider */}
      <div className="container mx-auto relative">
        {productList.length > 0 ? (
          <Slider {...settings}>
            {productList.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </Slider>
        ) : (
          <p className="text-center text-lg text-gray-500">No products found.</p>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProductsSection);
